<script setup>
import { reactive, onMounted, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import CSATDisplayTypeSelector from './components/CSATDisplayTypeSelector.vue';
import CSATTemplate from 'dashboard/components-next/template-preview/CSATTemplate.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
// import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');
// Computed to check if it's any type of WhatsApp channel (Cloud or Twilio)

const isUpdating = ref(false);
const selectedLabelValues = ref([]);
// const currentLabel = ref('');

const state = reactive({
  customURLEnable: false,
  csatSurveyEnabled: false,
  displayType: 'emoji',
  message: '',
  buttonUrl: '',
  surveyRuleOperator: 'contains',
  templateLanguage: '',
});

const templateStatus = ref(null);
const templateLoading = ref(false);
// const confirmDialog = ref(null);

// const filterTypes = [
//   {
//     label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.CONTAINS'),
//     value: 'contains',
//   },
//   {
//     label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.DOES_NOT_CONTAINS'),
//     value: 'does_not_contain',
//   },
// ];

// const labelOptions = computed(() =>
//   labels.value?.length
//     ? labels.value
//         .map(label => ({ label: label.title, value: label.title }))
//         .filter(label => !selectedLabelValues.value.includes(label.value))
//     : []
// );
const customUrlInitialized = ref(false);

watch(
  () => props.inbox?.csat_config?.button_url,
  buttonUrl => {
    if (customUrlInitialized.value) return;

    state.customURLEnable = !!buttonUrl;
    customUrlInitialized.value = true;
  },
  { immediate: true }
);

const initializeState = () => {
  if (!props.inbox) return;

  const { csat_survey_enabled, csat_config } = props.inbox;

  state.csatSurveyEnabled = csat_survey_enabled || false;

  if (!csat_config) return;

  const {
    display_type: displayType = CSAT_DISPLAY_TYPES.EMOJI,
    message = '',
    button_url: buttonUrl = '',
    language = 'en',
    survey_rules: surveyRules = {},
  } = csat_config;

  state.displayType = displayType;
  state.message = message;
  state.buttonUrl = buttonUrl;
  state.templateLanguage = language;
  state.surveyRuleOperator = surveyRules.operator || 'contains';

  selectedLabelValues.value = Array.isArray(surveyRules.values)
    ? [...surveyRules.values]
    : [];
};

const checkTemplateStatus = async () => {
  try {
    templateLoading.value = true;
    const response = await store.dispatch('inboxes/getCSATTemplateStatus', {
      inboxId: props.inbox.id,
    });

    // Handle case where template doesn't exist
    if (!response.template_exists && response.error === 'Template not found') {
      templateStatus.value = {
        template_exists: false,
        error: 'TEMPLATE_NOT_FOUND',
      };
    } else {
      templateStatus.value = response;
    }
  } catch (error) {
    templateStatus.value = {
      template_exists: false,
      error: 'API_ERROR',
    };
  } finally {
    templateLoading.value = false;
  }
};

onMounted(() => {
  initializeState();
  if (!labels.value?.length) store.dispatch('labels/get');
});

watch(() => props.inbox, initializeState, { immediate: true });

// const handleLabelSelect = value => {
//   if (!value || selectedLabelValues.value.includes(value)) {
//     return;
//   }

//   selectedLabelValues.value.push(value);
// };

const updateDisplayType = type => {
  state.displayType = type;
};

// const updateSurveyRuleOperator = operator => {
//   state.surveyRuleOperator = operator;
// };

// const removeLabel = label => {
//   const index = selectedLabelValues.value.indexOf(label);
//   if (index !== -1) {
//     selectedLabelValues.value.splice(index, 1);
//   }
// };

const updateInbox = async attributes => {
  const payload = {
    id: props.inbox.id,
    formData: false,
    ...attributes,
  };

  await store.dispatch('inboxes/updateInbox', payload);
};
const performSave = async () => {
  try {
    isUpdating.value = true;

    const csatConfig = {
      display_type: state.displayType,
      message: state.message,
      button_url: state.buttonUrl,
      language: state.templateLanguage,
      survey_rules: {
        operator: state.surveyRuleOperator,
        values: selectedLabelValues.value,
      },
    };

    await updateInbox({
      csat_survey_enabled: state.csatSurveyEnabled,
      csat_config: csatConfig,
    });

    useAlert(t('INBOX_MGMT.CSAT.API.SUCCESS_MESSAGE'));
    checkTemplateStatus();
  } catch (error) {
    useAlert(t('INBOX_MGMT.CSAT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
const isButtonUrlValid = computed(() => {
  if (!state.buttonUrl) return true;

  if (/\s/.test(state.buttonUrl)) return false;

  return /^https:\/\/[^\s]+\.[^\s]+$/.test(state.buttonUrl);
});
const hasMessageError = ref(false);

watch(
  () => state.message,
  () => {
    hasMessageError.value = false;
  }
);

const saveSettings = async () => {
  if (!state.customURLEnable) {
    state.buttonUrl = '';
  } else if (state.buttonUrl === '') {
    state.customURLEnable = false;
  }

  if (!state.message || !state.message.trim()) {
    hasMessageError.value = true;
    return;
  }

  await performSave();
};
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('INBOX_MGMT.CSAT.TITLE')"
      :description="$t('INBOX_MGMT.CSAT.SUBTITLE')"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="state.csatSurveyEnabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <WithLabel
          v-if="!isAnyWhatsAppChannel"
          :label="$t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL')"
          name="display_type"
        >
          <CSATDisplayTypeSelector
            :selected-type="state.displayType"
            @update="updateDisplayType"
          />
        </WithLabel>
        <WithLabel :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')" name="message">
          <div
            class="flex flex-col gap-4 w-full lg:flex-row lg:gap-6 items-stretch"
          >
            <div class="flex flex-col w-full lg:basis-3/5">
              <Editor
                v-model="state.message"
                :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
                :max-length="200"
                class="w-full flex-1 min-h-0"
                :message="
                  hasMessageError ? $t('INBOX_MGMT.CSAT.MESSAGE.REQUIRED') : ''
                "
                :message-type="hasMessageError ? 'error' : 'info'"
              />
            </div>

            <div class="flex flex-col w-full lg:basis-2/5 flex-shrink-0">
              <div
                class="flex flex-col justify-start items-center p-6 rounded-xl bg-n-slate-2 outline outline-1 outline-n-weak"
              >
                <p
                  class="inline-flex items-center text-sm font-medium text-n-slate-11"
                >
                  {{ $t('INBOX_MGMT.CSAT.MESSAGE_PREVIEW.LABEL') }}
                  <Icon
                    v-tooltip.top-end="
                      $t('INBOX_MGMT.CSAT.MESSAGE_PREVIEW.TOOLTIP')
                    "
                    icon="i-lucide-info"
                    class="flex-shrink-0 mx-1 size-4"
                  />
                </p>

                <CSATTemplate
                  :message="state.message"
                  button-text="Clique Aqui !"
                  class="pt-6"
                />
              </div>
            </div>
          </div>
        </WithLabel>
        <!--url botão-->
        <div>
          <div class="flex items-center justify-between my-2">
            <h4>{{ $t('INBOX_MGMT.CSAT.BUTTON_LINK.LABEL') }}</h4>
            <Switch v-model="state.customURLEnable" />
          </div>

          <div v-if="state.customURLEnable">
            <Input
              v-model="state.buttonUrl"
              type="text"
              :placeholder="$t('INBOX_MGMT.CSAT.BUTTON_LINK.PLACEHOLDER')"
              :message="
                !isButtonUrlValid
                  ? $t('INBOX_MGMT.CSAT.BUTTON_LINK.INVALID')
                  : ''
              "
              :message-type="!isButtonUrlValid ? 'error' : 'info'"
              class="w-full"
            />
            <p class="pb-1 text-sm not-italic text-n-slate-11 mt-2">
              {{ $t('INBOX_MGMT.CSAT.BUTTON_LINK.BUTTON_LEGEND') }}
            </p>
          </div>
        </div>

        <!-- <WithLabel
          :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL')"
          name="survey_rule"
        >
          <div class="mb-4">
            <span
              class="inline-flex flex-wrap gap-1.5 items-center text-sm text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_PREFIX') }}
              <FilterSelect
                v-model="state.surveyRuleOperator"
                variant="faded"
                :options="filterTypes"
                class="inline-flex shrink-0"
                @update:model-value="updateSurveyRuleOperator"
              />
              {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_SUFFIX') }}

              <NextButton
                v-for="label in selectedLabelValues"
                :key="label"
                sm
                faded
                slate
                trailing-icon
                :label="label"
                icon="i-lucide-x"
                class="inline-flex shrink-0"
                @click="removeLabel(label)"
              />
              <FilterSelect
                v-model="currentLabel"
                :options="labelOptions"
                :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.SELECT_PLACEHOLDER')"
                hide-label
                variant="faded"
                class="inline-flex shrink-0"
                @update:model-value="handleLabelSelect"
              />
            </span>
          </div>
        </WithLabel> -->
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
